import { pdkAxios } from '@/lib/pdk-axios.ts';
import { db } from '@/models/db';
import type {
  DeltaPacketRecord,
  FullHydrateResponse,
} from '@/models/hydrate.ts';
import { ProtectionPlanTermLength } from '@/models/registry.ts';
import type {
  BaseStore,
  StoreHydrationDelegator,
} from '@/models/stores/base.ts';
import type { ProtectionPlanTermLength as ProtectionPlanTermLengthType } from '@/models/types.ts';
import { makeAutoObservable, observable, type ObservableMap } from 'mobx';

class ProtectionPlanTermLengthStore
  implements BaseStore<ProtectionPlanTermLength, ProtectionPlanTermLengthType>
{
  store: ObservableMap<string, ProtectionPlanTermLength> = observable.map();

  constructor() {
    makeAutoObservable(this, { store: observable.shallow }, { autoBind: true });
  }

  applyDeltas({
    deltas,
    hydrateIndexedDB,
  }: {
    deltas: DeltaPacketRecord<ProtectionPlanTermLengthType>[];
    hydrateIndexedDB?: boolean;
  }) {
    for (const delta of deltas) {
      this.applyDelta({ delta, hydrateIndexedDB });
    }
  }

  applyDelta({
    delta,
    hydrateIndexedDB,
  }: {
    delta: DeltaPacketRecord<ProtectionPlanTermLengthType>;
    hydrateIndexedDB?: boolean;
  }) {
    if (hydrateIndexedDB) {
      db.protection_plan_term_lengths.put(delta.data, delta.record_id);
    }

    if (delta.action === 'delete') {
      this.store.delete(delta.record_id);
      return;
    }

    this.upsert(delta.data);
  }

  get all() {
    return Array.from(this.store.values());
  }

  getById(id: string): ProtectionPlanTermLength | undefined {
    return this.store.get(id);
  }

  add(input: ProtectionPlanTermLengthType) {
    const protectionPlanTermLength = new ProtectionPlanTermLength(input);
    this.store.set(protectionPlanTermLength.id, protectionPlanTermLength);
  }

  save(protectionPlanTermLength: ProtectionPlanTermLength) {
    this.store.delete(protectionPlanTermLength.id);
    this.store.set(protectionPlanTermLength.id, protectionPlanTermLength);
  }

  upsert(protectionPlanTermLength: ProtectionPlanTermLengthType) {
    const idToUse = protectionPlanTermLength.id;
    const existingProtectionPlanTermLength = this.getById(idToUse);
    if (existingProtectionPlanTermLength) {
      this.replace({ id: idToUse, protectionPlanTermLength });
    } else {
      this.add(protectionPlanTermLength);
    }
  }

  replace({
    id,
    protectionPlanTermLength,
  }: {
    id: string;
    protectionPlanTermLength: ProtectionPlanTermLengthType;
  }) {
    const existingProtectionPlanTermLength = this.getById(id);
    if (!existingProtectionPlanTermLength) return;
    existingProtectionPlanTermLength.term_length =
      protectionPlanTermLength.term_length;

    this.save(existingProtectionPlanTermLength);
  }
}

export const protectionPlanTermLengthStore =
  new ProtectionPlanTermLengthStore();

// TODO -- currently doing full hydrate every time, since protection plan term lengths cant be tracked via conventional delta sync
class ProtectionPlanTermLengthStoreHydrationDelegator
  implements StoreHydrationDelegator<ProtectionPlanTermLengthType>
{
  async hydrate({
    fullHydrateEndpoint,
  }: {
    fullHydrateEndpoint: string;
    deltaSyncEndpoint: string | null;
  }) {
    const resp =
      await pdkAxios.get<FullHydrateResponse<ProtectionPlanTermLengthType>>(
        fullHydrateEndpoint,
      );
    for (const protectionPlanTermLength of resp.data.data) {
      await db.protection_plan_term_lengths.put(
        protectionPlanTermLength,
        protectionPlanTermLength.id,
      );
      protectionPlanTermLengthStore.upsert(protectionPlanTermLength);
    }
  }
}

export const protectionPlanTermLengthStoreHydrationDelegator =
  new ProtectionPlanTermLengthStoreHydrationDelegator();
